"use client";

import { Button } from "@universe/ui/components/button";
import { Separator } from "@universe/ui/components/separator";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, Check, Info, Loader2, Shield, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { NoTranslate } from "@/components/no-translate";
import { CancelPaymentConfirmModal } from "@/components/payments/cancel-payment-confirm-modal";
import { useOtpAuthMethod } from "@/hooks/useOtpAuthMethod";
import { logError } from "@/lib/logError";
import { toast } from "@/lib/toast";
import { useCustomerAccount } from "@/providers";
import type { PaymentResourceType } from "@/types/payments";
import { handleOtpError } from "@/utils/otpHelpers";
import {
  useCancelBlockchainWithdrawalInstructionMutation,
  useCancelPaymentInstructionMutation,
  useConfirmBlockchainWithdrawalInstructionMutation,
  useConfirmPaymentInstructionMutation,
} from "@generated/redux/services/generated/api";
import {
  type ConfirmationRequirement,
  InstructionState,
  ProcessingState,
  ScaMethod,
} from "@generated/types";

import { usePayConfirmationSubmit } from "./use-pay-confirmation-submit";

/**
 * Shown while a confirm or cancel outcome is unconfirmed: both actions are
 * disabled, and this tells the customer why and what to do.
 */
function OutcomeUnconfirmedNotice() {
  const tCommon = useTranslations("common");
  return (
    <div
      role="status"
      data-testid="payment-outcome-unconfirmed"
      className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex gap-3 items-start"
    >
      <AlertCircle className="h-5 w-5 text-orange-800 flex-shrink-0 mt-0.5" />
      <p className="text-xs font-semibold text-orange-800">
        {tCommon("outcomeUnconfirmed")}
      </p>
    </div>
  );
}

interface PayConfirmationActionsProps {
  confirmationRequirements?: ConfirmationRequirement;
  isFetchingRequirements?: boolean;
  /**
   * Error from the confirmation-requirements query. When truthy, the terminal
   * "No confirmation required" branch is suppressed so stale cached data never
   * masks a backend failure.
   */
  requirementsError?: unknown;
  onRequestOtp: () => void;
  isOtpInProgress: boolean;
  scaMethod?: ScaMethod;
  onRefresh?: () => void | Promise<void>;
  /**
   * Reads the confirmation requirements again past the cache, for a confirm
   * or cancel that ended with no definite answer. Resolves `undefined` when
   * there is no requirements query to read; rejects when the read fails.
   */
  rereadRequirements?: () => Promise<ConfirmationRequirement | undefined>;
  isLoading?: boolean;
  paymentId?: string;
  onPaymentCancelled?: (paymentId: string) => void;
  instructionState?: InstructionState | string;
  processingState?: ProcessingState | string;
  resourceType?: PaymentResourceType;
}

export function PayConfirmationActions({
  confirmationRequirements,
  isFetchingRequirements = false,
  requirementsError,
  onRequestOtp,
  isOtpInProgress,
  scaMethod,
  onRefresh,
  rereadRequirements,
  isLoading = false,
  paymentId,
  onPaymentCancelled,
  instructionState,
  resourceType,
}: PayConfirmationActionsProps) {
  const t = useTranslations("pay.confirmationActions");
  const tCommon = useTranslations("common");
  const tPay = useTranslations("pay");
  const tPayments = useTranslations("payments");
  const tOtp = useTranslations("otp");
  const { customerAccount, userProfile } = useCustomerAccount();
  const { isOtpReady } = useOtpAuthMethod();

  // Call all hooks unconditionally to satisfy React's Rules of Hooks
  const [confirmPayment, { isLoading: isConfirmingPayment }] =
    useConfirmPaymentInstructionMutation();
  const [cancelPayment, { isLoading: isCancellingPayment }] =
    useCancelPaymentInstructionMutation();
  const [
    confirmBlockchainWithdrawal,
    { isLoading: isConfirmingBlockchainWithdrawal },
  ] = useConfirmBlockchainWithdrawalInstructionMutation();
  const [
    cancelBlockchainWithdrawal,
    { isLoading: isCancellingBlockchainWithdrawal },
  ] = useCancelBlockchainWithdrawalInstructionMutation();

  // Determine which resource type we're working with (default to payments)
  const isBlockchainWithdrawal = resourceType === "blockchain-withdrawals";
  const isPayment = !isBlockchainWithdrawal; // Default to payments if undefined

  // Use the appropriate hooks based on resource type
  const isConfirming = isPayment
    ? isConfirmingPayment
    : isConfirmingBlockchainWithdrawal;
  const isCancelling = isPayment
    ? isCancellingPayment
    : isCancellingBlockchainWithdrawal;

  // Delayed loading state to keep loading animation for 3 seconds after actual loading completes
  const [isDelayedLoading, setIsDelayedLoading] = useState(false);

  // Controls the Cancel-payment confirmation modal. The two branches never
  // render together, so they share it; the Unconfirmed branch also passes a
  // trigger button. Held state closes it (see onOutcomeUnconfirmed).
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  // True while the Confirm guard is latched; see usePayConfirmationSubmit.
  const [isConfirmLatched, setIsConfirmLatched] = useState(false);
  // True while a confirm or cancel outcome is unconfirmed: both actions stay
  // disabled until a different payment arrives or the page reloads.
  const [isOutcomeHeld, setIsOutcomeHeld] = useState(false);
  const hasHydratedRef = useRef(false);
  const previousSignaturesRef = useRef({
    sca: "",
    confirmers: "",
    actions: "",
  });

  // Track when loading conditions change to trigger delayed loading
  useEffect(() => {
    const isCurrentlyLoading =
      isFetchingRequirements || isOtpInProgress || (isLoading ?? false);

    if (isCurrentlyLoading && !isDelayedLoading) {
      // Start loading - set delayed loading to true
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronous setState to track loading state
      setIsDelayedLoading(true);
    } else if (!isCurrentlyLoading && isDelayedLoading) {
      // Loading completed - keep delayed loading for 1 more seconds
      const timeout = setTimeout(() => {
        setIsDelayedLoading(false);
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [isFetchingRequirements, isOtpInProgress, isLoading, isDelayedLoading]);

  const {
    scaRequired,
    confirmersRequired,
    currentConfirmers,
    availableConfirmers,
  } = confirmationRequirements ?? {
    scaRequired: undefined,
    confirmersRequired: 0,
    currentConfirmers: [],
    availableConfirmers: [],
  };

  const scaLoadingState =
    isFetchingRequirements || isOtpInProgress || isDelayedLoading;

  const sortedCurrentConfirmerIds = (currentConfirmers ?? [])
    .map((confirmer) => confirmer.id)
    .sort();

  const sortedAvailableConfirmerIds = (availableConfirmers ?? [])
    .map((confirmer) => confirmer.id)
    .sort();

  // Check if there's a completed SCA method that should be displayed
  const hasCompletedScaMethod =
    scaMethod === ScaMethod.TrustedBeneficiary ||
    scaMethod === ScaMethod.ManualCheck ||
    scaMethod === ScaMethod.RecurringTransaction ||
    scaMethod === ScaMethod.LowValue ||
    (scaMethod === ScaMethod.TwoFactor && !scaRequired);

  // Always render when confirmationRequirements is undefined (loading state)
  // or when in any loading state - show processing state by default
  const isInAnyLoadingState =
    isFetchingRequirements ||
    isOtpInProgress ||
    isDelayedLoading ||
    (isLoading ?? false);

  // Determine SCA display based on scaMethod OR scaRequired
  // Show SCA section when SCA is required OR when we have a scaMethod defined
  const showScaSection = scaRequired || scaMethod !== undefined;
  const isTrustedBeneficiary = scaMethod === ScaMethod.TrustedBeneficiary;
  const isCheckPassed =
    scaMethod === ScaMethod.ManualCheck ||
    scaMethod === ScaMethod.RecurringTransaction ||
    scaMethod === ScaMethod.LowValue;
  const isTwoFactorCompleted =
    scaMethod === ScaMethod.TwoFactor && !scaRequired;
  // Show button if SCA is required and method is NONE or undefined (not completed yet)
  const showScaButton =
    scaRequired === true &&
    (scaMethod === ScaMethod.None || scaMethod === undefined);
  // Only hide the SCA section when the backend has definitively told us SCA
  // is not needed. If the latest requirements refetch errored we might be
  // reading stale cached data — fall through to the loading state instead of
  // silently hiding the section.
  const hideScaSection =
    scaMethod === ScaMethod.None && scaRequired === false && !requirementsError;

  // Check if current user is already a confirmer
  const isUserAlreadyConfirmer =
    !!userProfile?.id &&
    currentConfirmers.some((confirmer) => confirmer.id === userProfile.id);
  const showConfirmButton =
    customerAccount?.confirmer === true && confirmersRequired > 0;
  const showCancelButton: boolean = true; // Always visible

  // Check if payment is CONFIRMED. The backend always returns enum casing
  // (InstructionState.Confirmed === "CONFIRMED"), so one check suffices.
  const isConfirmed = instructionState === InstructionState.Confirmed;

  // Backend has returned a definitive "nothing required" payload — do not keep
  // spinning. Treat both an absent scaMethod and ScaMethod.None as "no SCA set",
  // since both reach this branch with identical UX implications.
  // Requires no active query error — otherwise stale cached data could mask a
  // real backend failure and show a reassuring message over broken state.
  const hasNoScaMethodSet =
    scaMethod === undefined || scaMethod === ScaMethod.None;
  const isNoConfirmationRequired =
    confirmationRequirements !== undefined &&
    !requirementsError &&
    scaRequired === false &&
    confirmersRequired === 0 &&
    !hasCompletedScaMethod &&
    hasNoScaMethodSet;

  // Calculate signatures for useEffect (must be before early returns)
  const scaSignature = JSON.stringify({
    showScaSection,
    hideScaSection,
    isTrustedBeneficiary,
    isCheckPassed,
    isTwoFactorCompleted,
    showScaButton,
    scaLoadingState,
  });

  const confirmersSignature = JSON.stringify({
    confirmersRequired,
    current: sortedCurrentConfirmerIds,
    available: sortedAvailableConfirmerIds,
  });

  const actionsSignature = JSON.stringify({
    showConfirmButton,
    showCancelButton,
    isConfirming,
    isCancelling,
    isUserAlreadyConfirmer,
  });

  // Track animation states - computed in effect to avoid refs-during-render warning
  // Must be before early returns to satisfy React hooks rules
  const [shouldAnimate, setShouldAnimate] = useState({
    sca: false,
    confirmers: false,
    actions: false,
  });

  // Track signature changes (must be before early returns to satisfy React hooks rules)
  useEffect(() => {
    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;
    } else {
      // Update animation states when signatures change (only after initial hydration)
      setShouldAnimate({
        sca: previousSignaturesRef.current.sca !== scaSignature,
        confirmers:
          previousSignaturesRef.current.confirmers !== confirmersSignature,
        actions: previousSignaturesRef.current.actions !== actionsSignature,
      });
    }
    previousSignaturesRef.current = {
      sca: scaSignature,
      confirmers: confirmersSignature,
      actions: actionsSignature,
    };
  }, [scaSignature, confirmersSignature, actionsSignature]);

  // No pre-emptive `return null` here: even when SCA/confirmers are all zero we
  // still want to render the Cancel button and a clear "No confirmation required"
  // message so the user always has a recovery path. Prior versions returned null
  // when `scaMethod === undefined` which hid the Cancel button and left the user
  // on a dead page.

  // Confirm and Cancel move money, so each takes a submit-once guard; the
  // release policy (latch on success, re-arm on a host signal, release on a
  // definite error, re-read on an unknown one) lives in the hook.
  const { handleConfirmPayment, handleCancelPayment, rearmCancel } =
    usePayConfirmationSubmit({
      paymentId,
      isConfirmEditable:
        showConfirmButton && !isUserAlreadyConfirmer && !isConfirmed,
      confirm: (id) =>
        isPayment
          ? confirmPayment({
              paymentId: id,
              confirmInstructionRequest: {
                confirmed: true,
              },
            }).unwrap()
          : confirmBlockchainWithdrawal({
              blockchainWithdrawalId: id,
              confirmWithdrawalInstructionRequest: {
                confirmed: true,
              },
            }).unwrap(),
      cancel: (id) =>
        isPayment
          ? cancelPayment({ paymentId: id }).unwrap()
          : cancelBlockchainWithdrawal({
              blockchainWithdrawalId: id,
            }).unwrap(),
      onConfirmed: () => {
        toast.success(tPay("paymentConfirmed"));
        onRefresh?.();
      },
      onCancelled: (id) => {
        toast.success(tCommon("paymentCancelled"));
        onPaymentCancelled?.(id);
        onRefresh?.();
        setIsCancelConfirmOpen(false);
      },
      onConfirmFailed: (error) => {
        logError("Error confirming payment", error, {
          flow: "pay-confirmation",
          step: "confirm",
          isPayment,
          isBlockchainWithdrawal,
        });
        handleOtpError(error, "error", tCommon);
      },
      onCancelFailed: (error) => {
        logError("Error cancelling payment", error, {
          flow: "pay-confirmation",
          step: "cancel",
          isPayment,
          isBlockchainWithdrawal,
        });
        handleOtpError(error, "error", tCommon);
      },
      onConfirmLatchedChange: setIsConfirmLatched,
      // The confirm took if the requirements now list this user as a
      // confirmer. A read that returns nothing, or an unknown user, cannot
      // tell; a failed read rejects and the hook treats it the same way.
      rereadConfirm: async () => {
        const requirements = await rereadRequirements?.();
        if (!requirements || !userProfile?.id) return "undetermined";
        return requirements.currentConfirmers.some(
          (confirmer) => confirmer.id === userProfile.id
        )
          ? "applied"
          : "unchanged";
      },
      // The requirements carry no cancellation state, so no read here can
      // show whether the cancel took: a cancel with no definite answer always
      // ends held with the refresh notice, and the hook's applied/unchanged
      // cancel branches serve hosts that can read a cancellation. Refreshing
      // reloads the requirements and the payment lists so that what the
      // customer checks next is current; it decides nothing here.
      rereadCancel: async () => {
        await onRefresh?.();
        return "undetermined";
      },
      onOutcomeUnconfirmed: (action, error) => {
        logError("Payment action outcome unconfirmed", error, {
          flow: "pay-confirmation",
          step: action,
          isPayment,
          isBlockchainWithdrawal,
        });
        toast.warning(tCommon("outcomeUnconfirmed"));
        // Its Cancel payment button is refused while held; close it so the
        // notice is what the customer sees.
        setIsCancelConfirmOpen(false);
      },
      onOutcomeHeldChange: setIsOutcomeHeld,
    });

  // Opening the cancel modal is the deliberate "try again" gesture that
  // re-arms a cancel guard left latched by an earlier success.
  const handleCancelConfirmOpenChange = (open: boolean) => {
    if (open) rearmCancel();
    setIsCancelConfirmOpen(open);
  };

  // If payment is CONFIRMED, show the confirmation message. This fires for all
  // confirmed payments regardless of scaMethod — a previous `scaMethod !== None`
  // guard here left `isConfirmed && scaMethod === None` as an unrendered dead end.
  if (isConfirmed) {
    return (
      <div className="space-y-4 bg-brand-primary/5 rounded-lg p-4 md:px-8 md:py-6 lg:px-8 lg:py-5 border border-line">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <div className="flex items-center justify-center h-4 w-4 rounded-full bg-green-500">
            <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
          </div>
          <span>{tPayments("paymentConfirmedMessage")}</span>
        </div>
        {isOutcomeHeld && <OutcomeUnconfirmedNotice />}
        {/* Cancel button for confirmed payments */}
        <div className="flex flex-col gap-3">
          <Button
            variant="destructive"
            onClick={() => handleCancelConfirmOpenChange(true)}
            disabled={isCancelling || isOutcomeHeld}
            className="w-full font-semibold rounded-lg disabled:cursor-not-allowed"
          >
            {isCancelling ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{tPayments("cancelling")}</span>
              </span>
            ) : (
              <span>{tPayments("cancelPayment")}</span>
            )}
          </Button>
        </div>
        <CancelPaymentConfirmModal
          open={isCancelConfirmOpen}
          onOpenChange={handleCancelConfirmOpenChange}
          onConfirm={handleCancelPayment}
          isPending={isCancelling}
        />
      </div>
    );
  }

  const shouldAnimateSca = shouldAnimate.sca;
  const shouldAnimateConfirmers = shouldAnimate.confirmers;
  const shouldAnimateActions = shouldAnimate.actions;

  // Derive a single render branch for the SCA section so one AnimatePresence
  // can drive every transition. Previously, two parallel trees (one inside
  // AnimatePresence, one outside) duplicated the "no-confirmation" and
  // "loading" markup and left the outer branch without working exit animations.
  const showScaContent =
    showScaSection &&
    !isInAnyLoadingState &&
    (isTrustedBeneficiary ||
      isCheckPassed ||
      isTwoFactorCompleted ||
      showScaButton);

  type ScaRenderBranch = "content" | "none" | "loading" | "hidden";
  const scaRenderBranch: ScaRenderBranch = isNoConfirmationRequired
    ? "none"
    : showScaContent
      ? "content"
      : hideScaSection
        ? "hidden"
        : "loading";

  const scaNoneMotion = (
    <motion.div
      key="sca-none"
      data-testid="no-confirmation-required"
      className="space-y-2"
      initial={shouldAnimateSca ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Info className="h-5 w-5 text-muted-foreground" />
        <span>{t("noConfirmationRequired")}</span>
      </div>
    </motion.div>
  );

  const scaLoadingMotion = (
    <motion.div
      key="sca-loading"
      className="space-y-2"
      initial={shouldAnimateSca ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
        <span>{t("scaProcessing")}</span>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-4 bg-brand-primary/5 rounded-lg p-4 md:px-8 md:py-6 lg:px-8 lg:py-5 border border-line">
      {/* SCA Section — single AnimatePresence drives all transitions. */}
      <AnimatePresence mode="wait">
        {scaRenderBranch === "hidden" ? null : scaRenderBranch === "none" ? (
          scaNoneMotion
        ) : scaRenderBranch === "content" ? (
          <motion.div
            key="sca-content"
            className="space-y-2"
            initial={shouldAnimateSca ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            {isTrustedBeneficiary && (
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <div className="flex items-center justify-center h-4 w-4 rounded-full bg-green-500">
                  <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                </div>
                <span>{t("trustedBeneficiary")}</span>
              </div>
            )}

            {isCheckPassed && (
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <div className="flex items-center justify-center h-4 w-4 rounded-full bg-green-500">
                  <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                </div>
                <span>{t("checksPassed")}</span>
              </div>
            )}

            {isTwoFactorCompleted && (
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <div className="flex items-center justify-center h-4 w-4 rounded-full bg-green-500">
                  <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                </div>
                <span>{t("scaCompleted")}</span>
              </div>
            )}

            {showScaButton && (
              <>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Shield className="h-5 w-5 text-orange-500" />
                  <span>{t("scaRequired")}</span>
                </div>
                {isOtpReady ? (
                  <Button
                    onClick={onRequestOtp}
                    disabled={isOtpInProgress}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                  >
                    {isOtpInProgress ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{t("verifying")}</span>
                      </div>
                    ) : (
                      t("getOtp")
                    )}
                  </Button>
                ) : (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex gap-3 items-start">
                    <AlertCircle className="h-5 w-5 text-orange-800 flex-shrink-0 mt-0.5" />
                    <p className="text-xs font-semibold text-orange-800">
                      {tOtp("paymentOtpNotReadyWarning")}
                    </p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        ) : (
          scaLoadingMotion
        )}
      </AnimatePresence>

      {/* Separator only when the SCA section actually rendered something. */}
      {scaRenderBranch !== "hidden" && <Separator />}

      {/* Confirmers Required - Show list */}
      {confirmersRequired > 0 && (
        <motion.div
          className="space-y-3"
          initial={shouldAnimateConfirmers ? { opacity: 0, y: 5 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.2,
            delay: showScaSection && !hideScaSection ? 0.1 : 0,
          }}
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Users className="h-5 w-5 text-red-500" />
            <span>
              {t("confirmersRequired", {
                count: confirmersRequired,
              })}
            </span>
          </div>

          {currentConfirmers.length > 0 && (
            <motion.div
              initial={shouldAnimateConfirmers ? { opacity: 0, y: 5 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.2,
                delay: showScaSection && !hideScaSection ? 0.15 : 0.05,
              }}
            >
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-500" />
                <span>
                  {t("currentConfirmers", {
                    number: currentConfirmers.length,
                  })}
                </span>
              </h4>
              <ul className="space-y-1 ml-6">
                {currentConfirmers.map((confirmer) => (
                  <li
                    key={confirmer.id}
                    className="text-sm text-gray-800 flex items-center gap-2"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    <NoTranslate>{`${confirmer.firstName} ${confirmer.lastName}`}</NoTranslate>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {availableConfirmers.length > 0 && (
            <motion.div
              initial={shouldAnimateConfirmers ? { opacity: 0, y: 5 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.2,
                delay: 0.1,
              }}
            >
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span>
                  {t("availableConfirmers", {
                    count: availableConfirmers.length,
                  })}
                </span>
              </h4>
              <ul className="space-y-1 ml-6">
                {availableConfirmers.map((confirmer) => (
                  <li
                    key={confirmer.id}
                    className="text-sm text-gray-800 flex items-center gap-2"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                    <NoTranslate>{`${confirmer.firstName} ${confirmer.lastName}`}</NoTranslate>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Action Buttons - Stacked */}
      <motion.div
        initial={shouldAnimateActions ? { opacity: 0, y: 5 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.2,
          delay: 0.2,
        }}
        className="mt-3 flex flex-col gap-3"
      >
        {isOutcomeHeld && <OutcomeUnconfirmedNotice />}
        {showConfirmButton && (
          <Button
            onClick={handleConfirmPayment}
            disabled={
              isUserAlreadyConfirmer ||
              isConfirming ||
              isConfirmLatched ||
              isOutcomeHeld
            }
            className="w-full bg-emerald-300 hover:bg-emerald-500 text-primary font-semibold rounded-lg disabled:bg-gray-300 disabled:text-muted-foreground disabled:cursor-not-allowed"
          >
            {isConfirming ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t("confirming")}</span>
              </span>
            ) : isUserAlreadyConfirmer ? (
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <Check className="h-2 w-2 text-green-500" strokeWidth={4} />
                <span>{t("alreadyConfirmed")}</span>
              </span>
            ) : (
              <span>{t("confirm")}</span>
            )}
          </Button>
        )}
        {showCancelButton && (
          <CancelPaymentConfirmModal
            open={isCancelConfirmOpen}
            onOpenChange={handleCancelConfirmOpenChange}
            onConfirm={handleCancelPayment}
            isPending={isCancelling}
            trigger={
              <div>
                <Button
                  variant="destructive"
                  disabled={isCancelling || isOutcomeHeld}
                  className="w-full font-semibold rounded-lg disabled:cursor-not-allowed"
                >
                  {isCancelling ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{t("cancelling")}</span>
                    </span>
                  ) : (
                    <span>{t("cancelPayment")}</span>
                  )}
                </Button>
              </div>
            }
          />
        )}
      </motion.div>
    </div>
  );
}
